import { useState, useMemo, useEffect } from 'react'
import {
  Container, Title, Stack, Card, Text, Group, Button, Loader, Center,
  Badge, Table, Paper, Tabs, ScrollArea, Pagination, Select, Modal,
  Textarea, SimpleGrid, Accordion, ThemeIcon, Progress, Divider,
  ActionIcon, Tooltip, TextInput,
} from '@mantine/core'
import {
  IconRefresh, IconCheck, IconAlertTriangle, IconFlag, IconClock, IconEye,
  IconChecklist, IconShieldCheck, IconBug, IconSettings, IconStack2, IconSend,
  IconArrowRight, IconChevronLeft, IconChevronRight, IconUserCheck,
  IconAlertCircle, IconCircleCheck, IconTool, IconSearch,
} from '@tabler/icons-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { usePermissions } from '@/hooks/usePermissions'
import { OffenseDetailDialog } from '@/components/OffenseDetailDialog'
import { useOffensesBySearchId } from '@/hooks/useAnalytics'
import type { ComplianceDashboardData } from '@/types'
import {
  useFeatureGroupApi,
  type FeatureGroup,
  type SubmitReviewPayload,
  type SubmitExternalReviewPayload,
  type UpdateFlagPayload,
} from '@/hooks/useFeatureCombinationApi'

const fmt = (v: number | null | undefined) => (v ?? 0).toLocaleString()

const STATUS_COLORS: Record<string, string> = {
  PENDING_REVIEW: 'yellow',
  IN_PROGRESS: 'blue',
  APPROVED: 'green',
  FLAGGED: 'red',
  NEEDS_UPDATE: 'orange',
}

const STATUS_LABELS: Record<string, string> = {
  PENDING_REVIEW: 'Pending',
  IN_PROGRESS: 'In Progress',
  APPROVED: 'Approved',
  FLAGGED: 'Flagged',
  NEEDS_UPDATE: 'Needs Update',
}

const FLAG_COLORS: Record<string, string> = {
  FLAGGED: 'red',
  ACKNOWLEDGED: 'yellow',
  FIXED: 'green',
}

const EXTERNAL_STATUS_COLORS: Record<string, string> = {
  PENDING_EXTERNAL: 'yellow',
  CONFIRMED: 'green',
  REJECTED: 'red',
  DISPUTED: 'red',
  FLAGGED_FOR_REREVIEW: 'orange',
}

const EXTERNAL_STATUS_LABELS: Record<string, string> = {
  PENDING_EXTERNAL: 'Pending Review',
  CONFIRMED: 'Confirmed',
  REJECTED: 'Rejected',
  DISPUTED: 'Disputed',
  FLAGGED_FOR_REREVIEW: 'Flagged for Re-review',
}

const formatPct = (value: number | null) =>
  value !== null && value !== undefined ? `${(value * 100).toFixed(1)}%` : 'N/A'

const formatDate = (raw: any) => {
  if (!raw) return '-'
  const str = typeof raw === 'object' && raw !== null ? (raw as any).value : raw
  const d = new Date(str)
  return isNaN(d.getTime()) ? '-' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
}

export function FeatureCombinationQAPage() {
  const { isExpert, isAdmin } = usePermissions()
  const api = useFeatureGroupApi()
  const queryClient = useQueryClient()

  const [activeTab, setActiveTab] = useState<string>('pending')
  const [selectedGroup, setSelectedGroup] = useState<FeatureGroup | null>(null)
  const [detailModalOpen, setDetailModalOpen] = useState(false)
  const [filterState, setFilterState] = useState<string | null>(null)
  const [filterTypeClass, setFilterTypeClass] = useState<string | null>(null)
  const [filterQueue, setFilterQueue] = useState<string | null>(null)
  const [filterDecision, setFilterDecision] = useState<string | null>(null)
  const [filterExclusion, setFilterExclusion] = useState<string | null>(null)
  const [filterConsistency, setFilterConsistency] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const pageSize = 20

  const [selectedOffense, setSelectedOffense] = useState<ComplianceDashboardData | null>(null)
  const [offenseDialogOpen, setOffenseDialogOpen] = useState(false)

  // Direct search-by-search_id lookup (matches Reportability / Routing UX)
  const [searchIdInput, setSearchIdInput] = useState('')
  const [lookupSearchId, setLookupSearchId] = useState<string | null>(null)
  const {
    data: lookupData,
    isFetching: isLookupFetching,
    isError: isLookupError,
  } = useOffensesBySearchId(lookupSearchId)
  const lookupOffenses = (lookupData?.rows ?? []) as ComplianceDashboardData[]

  useEffect(() => {
    if (!lookupSearchId || isLookupFetching) return
    if (lookupOffenses.length > 0 && !offenseDialogOpen) {
      setSelectedOffense(lookupOffenses[0])
      setOffenseDialogOpen(true)
    } else if (lookupOffenses.length === 0 && !isLookupError) {
      notifications.show({
        title: 'No offenses found',
        message: `No offenses found for Search ID: ${lookupSearchId}`,
        color: 'yellow',
      })
      setLookupSearchId(null)
    } else if (isLookupError) {
      notifications.show({
        title: 'Lookup failed',
        message: `Could not load offenses for Search ID: ${lookupSearchId}`,
        color: 'red',
      })
      setLookupSearchId(null)
    }
  }, [lookupSearchId, isLookupFetching, isLookupError, lookupOffenses.length, offenseDialogOpen])

  const handleSearchIdLookup = () => {
    const trimmed = searchIdInput.trim()
    if (!trimmed) return
    setOffenseDialogOpen(false)
    setSelectedOffense(null)
    setLookupSearchId(trimmed)
  }

  const [reviewNotes, setReviewNotes] = useState('')
  const [reviewStatus, setReviewStatus] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)

  const [externalVerdict, setExternalVerdict] = useState<string | null>(null)
  const [externalNotes, setExternalNotes] = useState('')

  const statusFilter = useMemo(() => {
    if (isExpert) return 'APPROVED'
    if (activeTab === 'pending') return 'PENDING_REVIEW'
    if (activeTab === 'flagged') return 'FLAGGED'
    if (activeTab === 'approved') return 'APPROVED'
    if (activeTab === 'needs-update') return 'NEEDS_UPDATE'
    if (activeTab === 'external-reviews') return 'APPROVED'
    return undefined
  }, [activeTab, isExpert])

  const externalStatusFilter = useMemo(() => {
    if (!isExpert) return undefined
    if (activeTab === 'ext-pending' || activeTab === 'pending') return 'PENDING_EXTERNAL'
    if (activeTab === 'ext-confirmed') return 'CONFIRMED'
    if (activeTab === 'ext-disputed') return 'DISPUTED'
    return undefined
  }, [isExpert, activeTab])

  const isExternalReviewsTab = activeTab === 'external-reviews'

  const { data: groupsResponse, isLoading, refetch } = useQuery({
    queryKey: ['feature-groups', statusFilter, externalStatusFilter, filterState, filterTypeClass, filterQueue, filterDecision, filterExclusion, filterConsistency, page],
    queryFn: () => api.getGroups({
      review_status: statusFilter,
      external_review_status: externalStatusFilter,
      jurisdiction_state: filterState || undefined,
      type_class: filterTypeClass || undefined,
      dominant_queue: filterQueue || undefined,
      dominant_decision: filterDecision || undefined,
      exclusion_applied: filterExclusion === 'true' ? true : filterExclusion === 'false' ? false : undefined,
      is_consistent: filterConsistency === 'true' ? true : filterConsistency === 'false' ? false : undefined,
      page,
      limit: pageSize,
    }),
    enabled: isExpert || isAdmin,
    placeholderData: (prev) => prev,
  })

  const groups = groupsResponse?.data ?? []
  const serverTotal = groupsResponse?.total ?? 0

  const { data: stats } = useQuery({
    queryKey: ['feature-group-stats'],
    queryFn: api.getReviewStats,
    enabled: isExpert || isAdmin,
  })

  const { data: filterOptions } = useQuery({
    queryKey: ['feature-group-filters'],
    queryFn: api.getFilterOptions,
    enabled: isExpert || isAdmin,
  })

  const { data: sampleOffenses = [], isLoading: loadingOffenses } = useQuery({
    queryKey: ['sample-offenses', selectedGroup?.group_hash],
    queryFn: () => api.getSampleOffenses(selectedGroup!.group_hash),
    enabled: !!selectedGroup && detailModalOpen,
  })

  const submitMutation = useMutation({
    mutationFn: (params: { id: string; data: SubmitReviewPayload }) =>
      api.submitReview(params.id, params.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feature-groups'] })
      queryClient.invalidateQueries({ queryKey: ['feature-group-stats'] })
      setSubmitted(true)
    },
  })

  const externalReviewMutation = useMutation({
    mutationFn: (params: { id: string; data: SubmitExternalReviewPayload }) =>
      api.submitExternalReview(params.id, params.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feature-groups'] })
      setSubmitted(true)
    },
  })

  const flagMutation = useMutation({
    mutationFn: (params: { id: string; data: UpdateFlagPayload }) =>
      api.updateFlag(params.id, params.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feature-groups'] })
      queryClient.invalidateQueries({ queryKey: ['feature-group-stats'] })
    },
  })

  const displayGroups = groups

  const paginatedGroups = displayGroups
  const totalPages = Math.ceil(serverTotal / pageSize)

  const resetReviewForm = () => {
    setReviewNotes('')
    setReviewStatus(null)
    setExternalVerdict(null)
    setExternalNotes('')
    setSubmitted(false)
  }

  const selectGroup = (group: FeatureGroup) => {
    setSelectedGroup(group)
    setReviewStatus(group.review_status === 'PENDING_REVIEW' ? null : group.review_status)
    setReviewNotes(group.qa_notes || '')
    setExternalVerdict(group.external_qa_verdict || null)
    setExternalNotes(group.external_qa_notes || '')
    setSubmitted(false)
  }

  const goToNextGroup = () => {
    if (!selectedGroup) return
    const currentIndex = paginatedGroups.findIndex(g => g.group_hash === selectedGroup.group_hash)
    const next = paginatedGroups[currentIndex + 1]
    if (next) { selectGroup(next) }
    else if (page < totalPages) { setPage(page + 1); setDetailModalOpen(false); resetReviewForm() }
    else { setDetailModalOpen(false); resetReviewForm() }
  }

  const goToPrevGroup = () => {
    if (!selectedGroup) return
    const currentIndex = paginatedGroups.findIndex(g => g.group_hash === selectedGroup.group_hash)
    const prev = paginatedGroups[currentIndex - 1]
    if (prev) { selectGroup(prev) }
    else if (page > 1) { setPage(page - 1); setDetailModalOpen(false); resetReviewForm() }
  }

  const selectedPageIndex = useMemo(() => {
    if (!selectedGroup) return -1
    return paginatedGroups.findIndex(g => g.group_hash === selectedGroup.group_hash)
  }, [selectedGroup, paginatedGroups])

  const selectedGlobalIndex = selectedPageIndex >= 0 ? (page - 1) * pageSize + selectedPageIndex : -1
  const isFirstGroup = page === 1 && selectedPageIndex === 0
  const isLastGroup = page === totalPages && selectedPageIndex === paginatedGroups.length - 1

  const handleSubmitReview = () => {
    if (!selectedGroup) return
    submitMutation.mutate({
      id: selectedGroup.group_hash,
      data: {
        qa_notes: reviewNotes || undefined,
        review_status: (reviewStatus as SubmitReviewPayload['review_status']) || undefined,
      },
    })
  }

  const handleSubmitExternalReview = () => {
    if (!selectedGroup || !externalVerdict) return
    const statusMap: Record<string, SubmitExternalReviewPayload['external_review_status']> = {
      AGREE: 'CONFIRMED',
      DISAGREE: 'DISPUTED',
    }
    externalReviewMutation.mutate({
      id: selectedGroup.group_hash,
      data: {
        external_qa_verdict: externalVerdict as SubmitExternalReviewPayload['external_qa_verdict'],
        external_qa_notes: externalNotes || undefined,
        external_review_status: statusMap[externalVerdict] || 'CONFIRMED',
      },
    })
  }
  const exampleOffense = sampleOffenses[0] || null

  const reviewedPct = stats ? Math.round(((stats.approved + stats.flagged + stats.needs_update) / Math.max(stats.total, 1)) * 100) : 0

  if (!isExpert && !isAdmin) {
    return (
      <Container>
        <Text c="red">You do not have permission to access this page.</Text>
      </Container>
    )
  }

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" align="flex-end">
          <div>
            <Group gap="xs" mb={4}>
              <ThemeIcon variant="light" color="indigo" size="lg">
                <IconChecklist size={20} />
              </ThemeIcon>
              <Title order={2}>{isExpert ? 'External QA Review' : 'Decision Audit'}</Title>
            </Group>
            <Text size="sm" c="dimmed">
              {isExpert
                ? 'Review approved feature groups and provide your independent assessment'
                : 'Review unique feature groups, validate AI decision consistency, and manage flags'}
            </Text>
          </div>
          <Group gap="xs" align="flex-end">
            <TextInput
              label="Search ID"
              placeholder="Enter search ID..."
              value={searchIdInput}
              onChange={(e) => setSearchIdInput(e.currentTarget.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearchIdLookup() }}
              leftSection={<IconSearch size={16} />}
              w={260}
              size="sm"
            />
            <Button
              leftSection={isLookupFetching ? <Loader size={14} color="white" /> : <IconSearch size={14} />}
              onClick={handleSearchIdLookup}
              disabled={!searchIdInput.trim() || isLookupFetching}
            >
              Search
            </Button>
            <Button leftSection={<IconRefresh size={16} />} variant="light" onClick={() => refetch()}>
              Refresh
            </Button>
          </Group>
        </Group>

        {/* Stats dashboard */}
        {stats && !isExpert && (
          <SimpleGrid cols={{ base: 2, sm: 4, lg: 6 }}>
            <Card shadow="sm" p="md" radius="md" withBorder>
              <Group justify="space-between" align="flex-start">
                <div>
                  <Text size="xs" tt="uppercase" fw={600} c="dimmed">Awaiting Review</Text>
                  <Text size="xl" fw={700} mt={4}>{fmt(stats.pending_review)}</Text>
                </div>
                <ThemeIcon variant="light" color="yellow" size="xl" radius="md"><IconClock size={22} /></ThemeIcon>
              </Group>
              <Progress value={100 - reviewedPct} color="yellow" size="xs" mt="md" radius="xl" />
            </Card>
            <Card shadow="sm" p="md" radius="md" withBorder>
              <Group justify="space-between" align="flex-start">
                <div>
                  <Text size="xs" tt="uppercase" fw={600} c="dimmed">Approved</Text>
                  <Text size="xl" fw={700} mt={4} c="teal">{fmt(stats.approved)}</Text>
                </div>
                <ThemeIcon variant="light" color="teal" size="xl" radius="md"><IconShieldCheck size={22} /></ThemeIcon>
              </Group>
            </Card>
            <Card shadow="sm" p="md" radius="md" withBorder>
              <Group justify="space-between" align="flex-start">
                <div>
                  <Text size="xs" tt="uppercase" fw={600} c="dimmed">Flagged</Text>
                  <Text size="xl" fw={700} mt={4} c="red">{fmt(stats.flagged)}</Text>
                </div>
                <ThemeIcon variant="light" color="red" size="xl" radius="md"><IconBug size={22} /></ThemeIcon>
              </Group>
            </Card>
            <Card shadow="sm" p="md" radius="md" withBorder>
              <Group justify="space-between" align="flex-start">
                <div>
                  <Text size="xs" tt="uppercase" fw={600} c="dimmed">Needs Update</Text>
                  <Text size="xl" fw={700} mt={4} c="orange">{fmt(stats.needs_update)}</Text>
                </div>
                <ThemeIcon variant="light" color="orange" size="xl" radius="md"><IconSettings size={22} /></ThemeIcon>
              </Group>
            </Card>
            <Card shadow="sm" p="md" radius="md" withBorder>
              <Group justify="space-between" align="flex-start">
                <div>
                  <Text size="xs" tt="uppercase" fw={600} c="dimmed">Inconsistent</Text>
                  <Text size="xl" fw={700} mt={4} c="pink">{fmt(stats.inconsistent)}</Text>
                </div>
                <ThemeIcon variant="light" color="pink" size="xl" radius="md"><IconAlertCircle size={22} /></ThemeIcon>
              </Group>
            </Card>
            <Card shadow="sm" p="md" radius="md" withBorder>
              <Group justify="space-between" align="flex-start">
                <div>
                  <Text size="xs" tt="uppercase" fw={600} c="dimmed">Ext. Confirmed</Text>
                  <Text size="xl" fw={700} mt={4} c="cyan">{fmt(stats.externally_confirmed)}</Text>
                </div>
                <ThemeIcon variant="light" color="cyan" size="xl" radius="md"><IconUserCheck size={22} /></ThemeIcon>
              </Group>
            </Card>
          </SimpleGrid>
        )}

        {/* Expert stats */}
        {isExpert && stats && (
          <SimpleGrid cols={{ base: 2, sm: 4 }}>
            <Card shadow="sm" p="md" radius="md" withBorder>
              <Text size="xs" tt="uppercase" fw={600} c="dimmed">Pending My Review</Text>
              <Text size="xl" fw={700} mt={4}>{fmt(stats.pending_external)}</Text>
            </Card>
            <Card shadow="sm" p="md" radius="md" withBorder>
              <Text size="xs" tt="uppercase" fw={600} c="dimmed">Confirmed</Text>
              <Text size="xl" fw={700} mt={4} c="teal">{fmt(stats.externally_confirmed)}</Text>
            </Card>
            <Card shadow="sm" p="md" radius="md" withBorder>
              <Text size="xs" tt="uppercase" fw={600} c="dimmed">Disputed</Text>
              <Text size="xl" fw={700} mt={4} c="red">{fmt(stats.flagged)}</Text>
            </Card>
            <Card shadow="sm" p="md" radius="md" withBorder>
              <Text size="xs" tt="uppercase" fw={600} c="dimmed">Total</Text>
              <Text size="xl" fw={700} mt={4}>{fmt(stats.approved)}</Text>
            </Card>
          </SimpleGrid>
        )}

        {/* Filters + Tabs */}
        <Card shadow="sm" p="md" radius="md" withBorder>
          <Group justify="space-between" mb="sm">
            <Text fw={600} size="sm">Filter Groups</Text>
            <Group gap="xs">
              <Select placeholder="Queue" data={filterOptions?.queues?.map((q: string) => ({ value: q, label: q })) || []} value={filterQueue} onChange={(v) => { setFilterQueue(v); setPage(1) }} clearable size="xs" w={150} />
              <Select placeholder="Decision" data={filterOptions?.decisions?.map((d: string) => ({ value: d, label: d })) || []} value={filterDecision} onChange={(v) => { setFilterDecision(v); setPage(1) }} clearable size="xs" w={150} />
              <Select placeholder="State" data={filterOptions?.jurisdiction_states?.map((s: string) => ({ value: s, label: s })) || []} value={filterState} onChange={setFilterState} clearable size="xs" w={120} />
              <Select placeholder="Type Class" data={filterOptions?.type_classes?.map((s: string) => ({ value: s, label: s })) || []} value={filterTypeClass} onChange={setFilterTypeClass} clearable size="xs" w={140} />
              <Select placeholder="Exclusion" data={[{ value: 'true', label: 'Exclusion Applied' }, { value: 'false', label: 'No Exclusion' }]} value={filterExclusion} onChange={(v) => { setFilterExclusion(v); setPage(1) }} clearable size="xs" w={140} />
              <Select placeholder="Consistency" data={[{ value: 'true', label: 'Consistent' }, { value: 'false', label: 'Inconsistent' }]} value={filterConsistency} onChange={(v) => { setFilterConsistency(v); setPage(1) }} clearable size="xs" w={140} />
            </Group>
          </Group>
          <Tabs value={activeTab} onChange={(v) => { setActiveTab(v || (isExpert ? 'ext-pending' : 'pending')); setPage(1) }}>
            <Tabs.List>
              {isExpert ? (
                <>
                  <Tabs.Tab value="ext-pending" leftSection={<IconClock size={14} />}>Pending ({fmt(stats?.pending_external ?? 0)})</Tabs.Tab>
                  <Tabs.Tab value="ext-confirmed" leftSection={<IconCheck size={14} />}>Confirmed ({fmt(stats?.externally_confirmed ?? 0)})</Tabs.Tab>
                  <Tabs.Tab value="ext-disputed" leftSection={<IconFlag size={14} />}>Disputed</Tabs.Tab>
                  <Tabs.Tab value="ext-all" leftSection={<IconStack2 size={14} />}>All ({fmt(stats?.approved ?? 0)})</Tabs.Tab>
                </>
              ) : (
                <>
                  <Tabs.Tab value="pending" leftSection={<IconClock size={14} />}>Pending ({stats?.pending_review?.toLocaleString() ?? '...'})</Tabs.Tab>
                  <Tabs.Tab value="needs-update" leftSection={<IconAlertTriangle size={14} />}>Needs Update ({stats?.needs_update?.toLocaleString() ?? '...'})</Tabs.Tab>
                  <Tabs.Tab value="flagged" leftSection={<IconFlag size={14} />}>Flagged ({stats?.flagged?.toLocaleString() ?? '...'})</Tabs.Tab>
                  <Tabs.Tab value="approved" leftSection={<IconCheck size={14} />}>Approved ({stats?.approved?.toLocaleString() ?? '...'})</Tabs.Tab>
                  <Tabs.Tab value="external-reviews" leftSection={<IconUserCheck size={14} />}>External Reviews</Tabs.Tab>
                  <Tabs.Tab value="all" leftSection={<IconStack2 size={14} />}>All ({stats?.total?.toLocaleString() ?? '...'})</Tabs.Tab>
                </>
              )}
            </Tabs.List>
          </Tabs>
        </Card>

        {/* Group table */}
        {isLoading ? (
          <Center py="xl"><Loader /></Center>
        ) : (
          <Paper shadow="sm" withBorder radius="md">
            <ScrollArea type="auto">
              <Table striped highlightOnHover withTableBorder withColumnBorders style={{ minWidth: 2500 }}>
                <Table.Thead style={{ background: 'var(--mantine-color-gray-0)' }}>
                  <Table.Tr>
                    <Table.Th style={{ whiteSpace: 'nowrap', width: 50 }}>#</Table.Th>
                    <Table.Th style={{ whiteSpace: 'nowrap' }}>Status</Table.Th>
                    <Table.Th style={{ whiteSpace: 'nowrap' }}>Consistent</Table.Th>
                    {activeTab === 'flagged' && <Table.Th style={{ whiteSpace: 'nowrap' }}>Flag</Table.Th>}
                    {isExternalReviewsTab && <Table.Th style={{ whiteSpace: 'nowrap' }}>Ext. Status</Table.Th>}
                    <Table.Th style={{ whiteSpace: 'nowrap' }}>QA Notes</Table.Th>
                    <Table.Th style={{ whiteSpace: 'nowrap' }}>Type Class</Table.Th>
                    <Table.Th style={{ whiteSpace: 'nowrap' }}>Subtype</Table.Th>
                    <Table.Th style={{ whiteSpace: 'nowrap' }}>Charge Norm</Table.Th>
                    <Table.Th style={{ whiteSpace: 'nowrap' }}>Disp Class</Table.Th>
                    <Table.Th style={{ whiteSpace: 'nowrap' }}>Disp Norm</Table.Th>
                    <Table.Th style={{ whiteSpace: 'nowrap' }}>Age &le;7</Table.Th>
                    <Table.Th style={{ whiteSpace: 'nowrap' }}>Age Cat</Table.Th>
                    <Table.Th style={{ whiteSpace: 'nowrap' }}>Adjudication</Table.Th>
                    <Table.Th style={{ whiteSpace: 'nowrap' }}>Crime Cat</Table.Th>
                    <Table.Th style={{ whiteSpace: 'nowrap' }}>Court</Table.Th>
                    <Table.Th style={{ whiteSpace: 'nowrap' }}>State</Table.Th>
                    <Table.Th style={{ whiteSpace: 'nowrap' }}>Exclusion</Table.Th>
                    <Table.Th style={{ whiteSpace: 'nowrap' }}>Offenses</Table.Th>
                    <Table.Th style={{ whiteSpace: 'nowrap' }}>Accuracy</Table.Th>
                    <Table.Th style={{ whiteSpace: 'nowrap' }}>Mismatches</Table.Th>
                    <Table.Th style={{ whiteSpace: 'nowrap' }}>Decision</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {paginatedGroups.map((group, rowIndex) => (
                    <Table.Tr
                      key={group.group_hash}
                      style={{ cursor: 'pointer' }}
                      onClick={() => { selectGroup(group); setDetailModalOpen(true) }}
                    >
                      <Table.Td><Text size="xs" c="dimmed" ff="monospace">{(page - 1) * pageSize + rowIndex + 1}</Text></Table.Td>
                      <Table.Td>
                        <Badge color={STATUS_COLORS[group.review_status] || 'gray'} size="sm" variant="light">
                          {STATUS_LABELS[group.review_status] || group.review_status}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Tooltip label={group.is_consistent ? 'All offenses have same decision & rules' : `${group.distinct_decisions} decisions, ${group.distinct_rules_patterns} rule patterns`}>
                          <Badge size="xs" variant="light" color={group.is_consistent ? 'teal' : 'pink'}>
                            {group.is_consistent ? 'Yes' : 'No'}
                          </Badge>
                        </Tooltip>
                      </Table.Td>
                      {activeTab === 'flagged' && (
                        <Table.Td>
                          {group.flag_status && (
                            <Badge size="xs" variant="light" color={FLAG_COLORS[group.flag_status] || 'gray'}>
                              {group.flag_status}
                            </Badge>
                          )}
                        </Table.Td>
                      )}
                      {isExternalReviewsTab && (
                        <Table.Td>
                          <Badge color={EXTERNAL_STATUS_COLORS[group.external_review_status || 'PENDING_EXTERNAL'] || 'gray'} size="sm" variant="light">
                            {EXTERNAL_STATUS_LABELS[group.external_review_status || 'PENDING_EXTERNAL'] || 'Pending'}
                          </Badge>
                        </Table.Td>
                      )}
                      <Table.Td style={{ maxWidth: 200 }}><Text size="xs" c="dimmed" lineClamp={1}>{group.qa_notes || '-'}</Text></Table.Td>
                      <Table.Td style={{ whiteSpace: 'nowrap' }}><Text size="xs">{group.type_class || '-'}</Text></Table.Td>
                      <Table.Td style={{ whiteSpace: 'nowrap' }}><Text size="xs">{group.subtype_class || '-'}</Text></Table.Td>
                      <Table.Td style={{ whiteSpace: 'nowrap' }}><Text size="xs">{group.charge_norm || '-'}</Text></Table.Td>
                      <Table.Td style={{ whiteSpace: 'nowrap' }}><Text size="xs">{group.disp_class || '-'}</Text></Table.Td>
                      <Table.Td style={{ whiteSpace: 'nowrap' }}><Text size="xs">{group.disposition_norm || '-'}</Text></Table.Td>
                      <Table.Td><Text size="xs">{group.offense_age_lte_7 ? 'Yes' : 'No'}</Text></Table.Td>
                      <Table.Td style={{ whiteSpace: 'nowrap' }}><Text size="xs">{group.age_category || '-'}</Text></Table.Td>
                      <Table.Td style={{ whiteSpace: 'nowrap' }}><Text size="xs">{group.adjudication_category || '-'}</Text></Table.Td>
                      <Table.Td style={{ whiteSpace: 'nowrap' }}><Text size="xs">{group.crime_category || '-'}</Text></Table.Td>
                      <Table.Td style={{ whiteSpace: 'nowrap' }}><Text size="xs">{group.court_type || '-'}</Text></Table.Td>
                      <Table.Td><Text size="xs">{group.jurisdiction_state || '-'}</Text></Table.Td>
                      <Table.Td><Badge size="xs" variant="light" color={group.exclusion_applied ? 'red' : 'gray'}>{group.exclusion_applied ? 'Yes' : 'No'}</Badge></Table.Td>
                      <Table.Td><Text size="xs" fw={500}>{fmt(group.offense_count)}</Text></Table.Td>
                      <Table.Td><Text c={group.accuracy !== null && group.accuracy < 0.8 ? 'red' : 'teal'} fw={600} size="xs">{formatPct(group.accuracy)}</Text></Table.Td>
                      <Table.Td>
                        {group.mismatches > 0
                          ? <Text size="xs" fw={600} c="red">{fmt(group.mismatches)}</Text>
                          : <Text size="xs" c="dimmed">0</Text>}
                      </Table.Td>
                      <Table.Td style={{ whiteSpace: 'nowrap' }}><Text size="xs">{group.dominant_decision || '-'}</Text></Table.Td>
                    </Table.Tr>
                  ))}
                  {paginatedGroups.length === 0 && (
                    <Table.Tr>
                      <Table.Td colSpan={22}>
                        <Center py="xl">
                          <Stack align="center" gap="xs">
                            <ThemeIcon variant="light" color="gray" size="xl" radius="xl"><IconChecklist size={24} /></ThemeIcon>
                            <Text c="dimmed">No groups match your filters</Text>
                          </Stack>
                        </Center>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
            </ScrollArea>
            {totalPages > 1 && (
              <Center py="sm"><Pagination value={page} onChange={setPage} total={totalPages} size="sm" /></Center>
            )}
          </Paper>
        )}
      </Stack>

      {/* Inspection Modal */}
      <Modal
        opened={detailModalOpen}
        onClose={() => { setDetailModalOpen(false); resetReviewForm() }}
        title={
          <Group gap="xs">
            <ThemeIcon variant="light" color="indigo" size="md"><IconEye size={16} /></ThemeIcon>
            <div>
              <Title order={4}>Group Inspector</Title>
              {selectedGroup && (
                <Text size="xs" c="dimmed">{fmt(selectedGroup.offense_count)} offenses &middot; {formatPct(selectedGroup.accuracy)} accuracy &middot; {selectedGroup.mismatches} mismatches</Text>
              )}
            </div>
          </Group>
        }
        size="90%"
      >
        {selectedGroup && (
          <Stack gap="md">
            {/* Navigation */}
            <Group justify="space-between" px="xs">
              <ActionIcon variant="light" color="gray" size="lg" onClick={goToPrevGroup} disabled={isFirstGroup}><IconChevronLeft size={18} /></ActionIcon>
              <Text size="sm" fw={500} c="dimmed">{selectedGlobalIndex >= 0 ? `Item ${selectedGlobalIndex + 1} of ${fmt(serverTotal)}` : ''}</Text>
              <ActionIcon variant="light" color="gray" size="lg" onClick={goToNextGroup} disabled={isLastGroup}><IconChevronRight size={18} /></ActionIcon>
            </Group>

            {/* Badges: consistency, flags, exclusion */}
            <Group gap="xs">
              {!selectedGroup.is_consistent && (
                <Badge color="pink" variant="light" size="sm" leftSection={<IconAlertCircle size={12} />}>
                  Inconsistent ({selectedGroup.distinct_decisions} decisions, {selectedGroup.distinct_rules_patterns} rule patterns)
                </Badge>
              )}
              {selectedGroup.is_consistent && (
                <Badge color="teal" variant="light" size="sm" leftSection={<IconCircleCheck size={12} />}>
                  Consistent
                </Badge>
              )}
              {selectedGroup.exclusion_applied && (
                <Badge color="red" variant="light" size="sm" leftSection={<IconFlag size={12} />}>
                  Exclusion Filter Applied
                </Badge>
              )}
              {selectedGroup.flag_status && (
                <Badge color={FLAG_COLORS[selectedGroup.flag_status] || 'gray'} variant="light" size="sm" leftSection={<IconTool size={12} />}>
                  Flag: {selectedGroup.flag_status}
                </Badge>
              )}
              {!selectedGroup.can_approve && selectedGroup.review_status !== 'APPROVED' && (
                <Badge color="gray" variant="light" size="sm">Cannot Approve</Badge>
              )}
            </Group>

            {/* Flag lifecycle controls (for flagged groups) */}
            {isAdmin && selectedGroup.flag_status && selectedGroup.flag_status !== 'FIXED' && (
              <Card withBorder radius="md" p="sm" bg="red.0">
                <Group justify="space-between">
                  <Group gap="xs">
                    <IconTool size={16} />
                    <Text size="sm" fw={600}>Flag Lifecycle</Text>
                    <Badge size="sm" color={FLAG_COLORS[selectedGroup.flag_status] || 'gray'}>{selectedGroup.flag_status}</Badge>
                  </Group>
                  <Group gap="xs">
                    {selectedGroup.flag_status === 'FLAGGED' && (
                      <Button size="xs" variant="light" color="yellow" onClick={() => flagMutation.mutate({ id: selectedGroup.group_hash, data: { flag_status: 'ACKNOWLEDGED' } })} loading={flagMutation.isPending}>
                        Acknowledge
                      </Button>
                    )}
                    {selectedGroup.flag_status === 'ACKNOWLEDGED' && (
                      <Button size="xs" variant="light" color="green" onClick={() => flagMutation.mutate({ id: selectedGroup.group_hash, data: { flag_status: 'FIXED' } })} loading={flagMutation.isPending}>
                        Mark Fixed
                      </Button>
                    )}
                  </Group>
                </Group>
                {selectedGroup.flag_notes && <Text size="xs" mt="xs" c="dimmed">{selectedGroup.flag_notes}</Text>}
              </Card>
            )}

            {/* Sample offense */}
            {loadingOffenses ? (
              <Center py="md"><Loader size="sm" /></Center>
            ) : exampleOffense ? (
              <>
              <Card withBorder radius="md" p="md" bg="gray.0">
                <Group gap="xs" mb="sm">
                  <ThemeIcon variant="light" color="blue" size="sm"><IconEye size={14} /></ThemeIcon>
                  <Text size="sm" fw={700}>Sample Record</Text>
                  <Text size="xs" c="dimmed">One offense from this group</Text>
                </Group>
                <SimpleGrid cols={3} spacing="xs" verticalSpacing={6}>
                  {[
                    { label: 'Search ID', value: exampleOffense.search_id, feature: false },
                    { label: 'Offense ID', value: exampleOffense.offense_id, feature: false },
                    { label: 'Case Number', value: exampleOffense.case_number, feature: false },
                    { label: 'Charge', value: exampleOffense.charge, feature: false },
                    { label: 'Charge Description', value: exampleOffense.charge_description, feature: false },
                    { label: 'Charge Normalized', value: exampleOffense.charge_norm, feature: true },
                    { label: 'Disposition', value: exampleOffense.disposition, feature: false },
                    { label: 'Disposition Class', value: exampleOffense.disp_class, feature: true },
                    { label: 'Disposition Normalized', value: exampleOffense.disposition_norm, feature: true },
                    { label: 'Offense Type', value: exampleOffense.offense_type, feature: false },
                    { label: 'Type Class', value: exampleOffense.type_class, feature: true },
                    { label: 'Subtype Class', value: exampleOffense.subtype_class, feature: true },
                    { label: 'Offense Date', value: exampleOffense.offense_date, feature: false },
                    { label: 'Arrest Date', value: exampleOffense.arrest_date, feature: false },
                    { label: 'Disposition Date', value: exampleOffense.disposition_date, feature: false },
                    { label: 'File Date', value: exampleOffense.file_date, feature: false },
                    { label: 'Offense Age', value: exampleOffense.offense_age, feature: true },
                    { label: 'Age at Offense', value: exampleOffense.age_at_offense, feature: false },
                    { label: 'Age Category', value: exampleOffense.age_category, feature: true },
                    { label: 'Offense Status', value: exampleOffense.offense_status, feature: false },
                    { label: 'Crime Category', value: exampleOffense.crime_category, feature: true },
                    { label: 'Adjudication', value: exampleOffense.adjudication_category, feature: true },
                    { label: 'Court Type', value: exampleOffense.court_type, feature: true },
                    { label: 'Jurisdiction', value: exampleOffense.jurisdiction, feature: false },
                    { label: 'Jurisdiction State', value: exampleOffense.jurisdiction_state, feature: false },
                    { label: 'Exclusion Applied', value: exampleOffense.exclusion_applied === null ? null : exampleOffense.exclusion_applied ? 'Yes' : 'No', feature: true },
                  ].map((field) => {
                    let raw = field.value
                    if (raw !== null && typeof raw === 'object' && !Array.isArray(raw)) {
                      raw = (raw as any).value ?? JSON.stringify(raw)
                    }
                    const v = raw === null || raw === undefined || raw === '' ? 'N/A' : String(raw)
                    return (
                      <div key={field.label} style={field.feature ? { background: 'var(--mantine-color-blue-0)', borderRadius: 6, padding: '4px 8px', border: '1px solid var(--mantine-color-blue-1)' } : { padding: '4px 8px' }}>
                        <Text size="xs" c={field.feature ? 'blue.7' : 'dimmed'} fw={field.feature ? 600 : undefined}>{field.label}</Text>
                        <Text size="sm" lineClamp={1} c={field.feature ? 'blue.9' : undefined} fw={field.feature ? 500 : undefined}>{v}</Text>
                      </div>
                    )
                  })}
                </SimpleGrid>
              </Card>

              <Card withBorder radius="md" p="md">
                <Group gap="xs" mb="sm">
                  <ThemeIcon variant="light" color="grape" size="sm"><IconChecklist size={14} /></ThemeIcon>
                  <Text size="sm" fw={700}>Decisions</Text>
                </Group>
                <SimpleGrid cols={2} spacing="md">
                  <Card withBorder radius="sm" p="sm" bg="indigo.0" style={{ border: '1px solid var(--mantine-color-indigo-2)' }}>
                    <Text size="xs" fw={600} c="indigo.7" mb={4}>AI (Compliance Engine)</Text>
                    <Group gap="xs" mb={4}>
                      <Text size="xs" c="dimmed">Decision:</Text>
                      <Badge size="sm" variant="light" color="indigo">{exampleOffense.ce_decision || 'N/A'}</Badge>
                    </Group>
                    <div style={{ marginBottom: 4 }}>
                      <Text size="xs" c="dimmed">Rules Applied</Text>
                      <Text size="sm" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{exampleOffense.ce_rules_applied || 'N/A'}</Text>
                    </div>
                    <div>
                      <Text size="xs" c="dimmed">Rationale</Text>
                      <Text size="sm" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{exampleOffense.ce_rationale || 'N/A'}</Text>
                    </div>
                  </Card>
                  <Card withBorder radius="sm" p="sm" bg="teal.0" style={{ border: '1px solid var(--mantine-color-teal-2)' }}>
                    <Text size="xs" fw={600} c="teal.7" mb={4}>UBS (Ground Truth)</Text>
                    <Group gap="xs" mb={4}>
                      <Text size="xs" c="dimmed">Decision:</Text>
                      <Badge size="sm" variant="light" color="teal">{exampleOffense.ground_truth || 'N/A'}</Badge>
                    </Group>
                    <div>
                      <Text size="xs" c="dimmed">Notes</Text>
                      <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{exampleOffense.label_notes || 'N/A'}</Text>
                    </div>
                  </Card>
                </SimpleGrid>
              </Card>
              </>
            ) : (
              <Center py="lg">
                <Stack align="center" gap="xs">
                  <ThemeIcon variant="light" color="gray" size="xl" radius="xl"><IconStack2 size={24} /></ThemeIcon>
                  <Text c="dimmed" size="sm">No offenses found for this group</Text>
                </Stack>
              </Center>
            )}

            {/* Related offenses */}
            <Accordion variant="separated" radius="md">
              <Accordion.Item value="sample-offenses">
                <Accordion.Control icon={<IconStack2 size={18} />}>
                  <Group gap="xs">
                    <Text size="sm" fw={600}>Related Offenses</Text>
                    <Badge size="sm" variant="light" color="gray">{sampleOffenses.length}</Badge>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  {loadingOffenses ? <Center py="md"><Loader size="sm" /></Center> : (
                    <ScrollArea h={300}>
                      <Table striped highlightOnHover withTableBorder withColumnBorders>
                        <Table.Thead style={{ background: 'var(--mantine-color-gray-0)' }}>
                          <Table.Tr>
                            <Table.Th>Offense ID</Table.Th>
                            <Table.Th>Charge</Table.Th>
                            <Table.Th>Disposition</Table.Th>
                            <Table.Th>AI Decision</Table.Th>
                            <Table.Th>Ground Truth</Table.Th>
                            <Table.Th>Match</Table.Th>
                            <Table.Th>Confidence</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {sampleOffenses.map((o: any) => (
                            <Table.Tr key={o.offense_id} onClick={() => { setSelectedOffense(o as ComplianceDashboardData); setOffenseDialogOpen(true) }} style={{ cursor: 'pointer' }}>
                              <Table.Td><Text size="xs" ff="monospace">{o.offense_id}</Text></Table.Td>
                              <Table.Td><Text size="xs" lineClamp={1}>{o.charge || '-'}</Text></Table.Td>
                              <Table.Td><Text size="xs" lineClamp={1}>{o.disposition || '-'}</Text></Table.Td>
                              <Table.Td><Text size="xs">{o.ce_decision || 'N/A'}</Text></Table.Td>
                              <Table.Td><Text size="xs">{o.ground_truth || 'N/A'}</Text></Table.Td>
                              <Table.Td><Badge size="xs" variant="light" color={o.match_status === 'MATCH' ? 'teal' : o.match_status === 'MISMATCH' ? 'red' : 'gray'}>{o.match_status || '-'}</Badge></Table.Td>
                              <Table.Td><Text size="xs">{o.ce_confidence?.toFixed(2) ?? 'N/A'}</Text></Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    </ScrollArea>
                  )}
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>

            {/* External QA details (admin view) */}
            {isAdmin && selectedGroup.external_review_status && (
              <Card withBorder radius="md" p="md" bg={selectedGroup.external_review_status === 'CONFIRMED' ? 'teal.0' : selectedGroup.external_review_status === 'DISPUTED' || selectedGroup.external_review_status === 'REJECTED' ? 'red.0' : 'gray.0'}>
                <Group gap="xs" mb="sm">
                  <ThemeIcon variant="light" color="violet" size="sm"><IconUserCheck size={14} /></ThemeIcon>
                  <Text size="sm" fw={700}>External QA Review</Text>
                  <Badge size="xs" color={EXTERNAL_STATUS_COLORS[selectedGroup.external_review_status] || 'gray'} variant="light">
                    {EXTERNAL_STATUS_LABELS[selectedGroup.external_review_status] || selectedGroup.external_review_status}
                  </Badge>
                </Group>
                <SimpleGrid cols={3} spacing="xs">
                  <div><Text size="xs" c="dimmed">Verdict</Text><Text size="sm" fw={500}>{selectedGroup.external_qa_verdict || 'N/A'}</Text></div>
                  <div><Text size="xs" c="dimmed">Reviewer</Text><Text size="sm" fw={500}>{selectedGroup.external_reviewed_by_name || selectedGroup.external_reviewed_by_email || 'N/A'}</Text></div>
                  <div><Text size="xs" c="dimmed">Reviewed At</Text><Text size="sm">{formatDate(selectedGroup.external_reviewed_at)}</Text></div>
                </SimpleGrid>
                {selectedGroup.external_qa_notes && <div style={{ marginTop: 4 }}><Text size="xs" c="dimmed">Notes</Text><Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{selectedGroup.external_qa_notes}</Text></div>}
              </Card>
            )}

            <Divider />

            {/* Review form */}
            {isExpert ? (
              <>
                {selectedGroup.qa_notes && (
                  <Card withBorder radius="md" p="md" bg="gray.0">
                    <Group gap="xs" mb="sm">
                      <ThemeIcon variant="light" color="gray" size="sm"><IconShieldCheck size={14} /></ThemeIcon>
                      <Text size="sm" fw={700}>Internal QA Assessment</Text>
                      <Badge size="xs" color={STATUS_COLORS[selectedGroup.review_status] || 'gray'} variant="light">{STATUS_LABELS[selectedGroup.review_status] || selectedGroup.review_status}</Badge>
                    </Group>
                    <Text size="sm" c="dimmed" style={{ whiteSpace: 'pre-wrap' }}>{selectedGroup.qa_notes}</Text>
                  </Card>
                )}
                <Card withBorder radius="md" p="md">
                  <Group gap="xs" mb="sm"><ThemeIcon variant="light" color="violet" size="sm"><IconSend size={14} /></ThemeIcon><Text size="sm" fw={700}>Your External Review</Text></Group>
                  <Stack gap="sm">
                    <Select label="Verdict" placeholder="Do you agree with the internal QA assessment?" data={[{ value: 'AGREE', label: 'Agree' }, { value: 'DISAGREE', label: 'Disagree' }]} value={externalVerdict} onChange={setExternalVerdict} clearable />
                    <Textarea label="Notes" placeholder="Any observations..." minRows={2} value={externalNotes} onChange={(e) => setExternalNotes(e.currentTarget.value)} />
                    <Group justify="flex-end">
                      {submitted
                        ? <Button rightSection={<IconArrowRight size={14} />} onClick={goToNextGroup} variant="gradient" gradient={{ from: 'teal', to: 'cyan' }}>Next Group</Button>
                        : <Button leftSection={<IconSend size={14} />} onClick={handleSubmitExternalReview} loading={externalReviewMutation.isPending} disabled={!externalVerdict} variant="gradient" gradient={{ from: 'indigo', to: 'violet' }}>Submit External Review</Button>}
                    </Group>
                  </Stack>
                </Card>
              </>
            ) : (
              <Card withBorder radius="md" p="md">
                <Group gap="xs" mb="sm"><ThemeIcon variant="light" color="violet" size="sm"><IconSend size={14} /></ThemeIcon><Text size="sm" fw={700}>Your Assessment</Text></Group>
                <Stack gap="sm">
                  <Select
                    label="Verdict"
                    placeholder="How is the AI handling this group?"
                    data={[
                      { value: 'APPROVED', label: 'Approved', disabled: !selectedGroup.can_approve },
                      { value: 'FLAGGED', label: 'Flagged — AI is making errors' },
                      { value: 'NEEDS_UPDATE', label: 'Needs Update — Improvements needed' },
                    ]}
                    value={reviewStatus}
                    onChange={setReviewStatus}
                    clearable
                  />
                  {!selectedGroup.can_approve && <Text size="xs" c="orange">This group cannot be approved because it is inconsistent or flagged.</Text>}
                  <Textarea label="Notes" placeholder="Any observations or context..." minRows={2} value={reviewNotes} onChange={(e) => setReviewNotes(e.currentTarget.value)} />
                  <Group justify="flex-end">
                    {submitted
                      ? <Button rightSection={<IconArrowRight size={14} />} onClick={goToNextGroup} variant="gradient" gradient={{ from: 'teal', to: 'cyan' }}>Next Group</Button>
                      : <Button leftSection={<IconSend size={14} />} onClick={handleSubmitReview} loading={submitMutation.isPending} variant="gradient" gradient={{ from: 'indigo', to: 'violet' }}>Submit Assessment</Button>}
                  </Group>
                </Stack>
              </Card>
            )}
          </Stack>
        )}
      </Modal>

      <OffenseDetailDialog
        opened={offenseDialogOpen}
        onClose={() => {
          setOffenseDialogOpen(false)
          setSelectedOffense(null)
          setLookupSearchId(null)
        }}
        offense={selectedOffense}
        allOffensesInSearch={
          lookupSearchId && selectedOffense?.search_id === lookupSearchId
            ? lookupOffenses
            : (sampleOffenses.filter((o: any) => o.search_id === selectedOffense?.search_id) as unknown as ComplianceDashboardData[])
        }
      />
    </Container>
  )
}
